import DoneIcon from '@mui/icons-material/Done'
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import CustomSnackbar from '@/common/components/CustomSnackbar/CustomSnackbar'
import { useChangeUiLanguage } from '@/hooks/useChangeUiLanguage'
import ExpandIcon from '@/icons/ExpandIcon'
import i18n from '@/locales/i18n'
import { useUiLanguageLabel } from '@/locales/uiLanguages'
import { useListLanguagesQuery } from '@/services/api/tenant/languages'
import CustomPalette from '@/theme/CustomPalette'
import { menuContainer, selectedContainer, selectLanguageContainer } from './LanguageSelectorStyles'

type LanguageSelectorProps = {
  selectedLanguage: string
  setSelectedLanguage: (value: string) => void
}

type Language = {
  tag: string
  display_name: string
}

export const LanguageSelector = ({ selectedLanguage, setSelectedLanguage }: LanguageSelectorProps) => {
  const { t } = useTranslation('general')
  const languageLabel = useUiLanguageLabel()
  const { data: languages = [] } = useListLanguagesQuery({ displayInUi: true }) as { data: Language[] }
  const { changeUiLanguage, showLanguageError, dismissLanguageError } = useChangeUiLanguage()

  const currentLanguage = i18n.language

  useEffect(() => {
    if (currentLanguage) {
      setSelectedLanguage(currentLanguage)
    }
  }, [currentLanguage, setSelectedLanguage])

  const isLanguageAvailable = languages.some((lang) => lang.tag === selectedLanguage)
  const selectValue = isLanguageAvailable ? selectedLanguage : ''

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const newLanguage = event.target.value
    if (!newLanguage || newLanguage === selectedLanguage) return

    if (await changeUiLanguage(newLanguage)) {
      setSelectedLanguage(newLanguage)
    }
  }

  const renderSelectedLanguage = () => {
    const selected = languages.find((lang) => lang.tag === selectedLanguage)
    return (
      <Typography
        sx={{
          ...selectedContainer,
        }}>
        {selected ? languageLabel(selected.tag, selected.display_name) : ''}
      </Typography>
    )
  }

  return (
    <FormControl sx={{ color: CustomPalette.NightBlue200, mt: -1.5, ml: 2, mr: 6 }}>
      <InputLabel id="language-select-label" />
      <Select
        labelId="language-select-label"
        id="language-select"
        variant="standard"
        value={selectValue}
        onChange={handleChange}
        IconComponent={ExpandIcon}
        renderValue={renderSelectedLanguage}
        sx={{
          ...selectLanguageContainer,
        }}>
        {languages.map(({ tag, display_name }) => (
          <MenuItem
            key={tag}
            value={tag}
            sx={{
              ...menuContainer,
              backgroundColor: selectedLanguage === tag ? CustomPalette.Air : CustomPalette.SecondaryCloud,
              '&:hover': { backgroundColor: CustomPalette.Air },
            }}>
            <span style={{ flexGrow: 1 }}>{languageLabel(tag, display_name)}</span>
            {selectedLanguage === tag && <DoneIcon sx={{ ml: 2 }} />}
          </MenuItem>
        ))}
      </Select>
      {showLanguageError && (
        <CustomSnackbar
          open={showLanguageError}
          onClose={dismissLanguageError}
          message={t('error_contact_support')}
          severity="error"
        />
      )}
    </FormControl>
  )
}
